import { useCallback, useEffect, useRef, useState } from "react";
import Localization from "../../../../localization";
import { useApi, useApp } from "../../../../hooks";
import { GroupApi } from "../../../../apis";
import useViewModel from "../../viewModel";
import { SVGs } from "../../../../assets";
import Invite from "./invite";
import { Flex } from "antd";
import {
  ContentLoader,
  Button,
  Modal,
  Icon,
  List,
  Text,
} from "../../../../components";

const ClosestCircle = ({ onDone = () => {} }) => {
  const { group, getClosestCircleMembers } = useViewModel();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const inviteModalRef = useRef();
  const { execute } = useApi();
  const { modal } = useApp();

  useEffect(() => {
    getData({ screenLoad: true });
  }, []);

  const getData = async ({ screenLoad = false } = {}) => {
    setLoading(screenLoad);
    try {
      const members = await getClosestCircleMembers();
      setMembers(members);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const sendInvites = async (emails) => {
    await execute(() => GroupApi.sendInvites(group?.id, emails));
    await getData();
    inviteModalRef.current.close();
  };

  const removeMember = async (memberUuid) => {
    await execute(() => GroupApi.removeMember(group?.id, memberUuid));
    await getData();
  };

  const renderItem = useCallback((member) => {
    const alias = !member.invited ? member.alias : member.email;
    return (
      <List.Item
        extra={
          !member.owner &&
          !member.invited && (
            <Text level={3} type="secondary">
              <Icon
                component={SVGs.deleteOutlinedComp}
                onClick={() =>
                  modal.confirm({
                    onOk: async (modal) => {
                      await removeMember(member.userId);
                      modal.destroy();
                    },
                    title: Localization.general.delete_recepient,
                    description: Localization.formatString(
                      Localization.service.alarmSMS_modal_del_desc,
                      <Text
                        level="text"
                        weight={600}
                        color="inherit"
                        children={alias}
                      />
                    ),
                  })
                }
              />
            </Text>
          )
        }
        children={
          <List.Item.Meta
            disabled={member.invited}
            description={
              member.invited
                ? Localization.general.invitation_sent
                : [
                    <Text
                      color="inherit"
                      className="d-none-mobile"
                      children={
                        !member.phone
                          ? member.email
                          : `${member.email} • ${member.phone}`
                      }
                    />,
                    <Text
                      className="d-none d-block-mobile"
                      color="inherit"
                      children={member.email}
                    />,
                    <Text
                      className="d-none d-block-mobile"
                      color="inherit"
                      children={member.phone}
                    />,
                  ]
            }
            title={
              !member.owner ? (
                alias ?? member.email
              ) : (
                <Flex gap={".5rem"} align="center">
                  <SVGs.star />
                  <Text
                    level={5}
                    weight={"700"}
                    children={`${alias} (${Localization.general.owner})`}
                  />
                </Flex>
              )
            }
          />
        }
      />
    );
  }, []);

  if (loading)
    return (
      <ContentLoader
        skeletons={[{ height: "6.7rem" }, { height: "12rem", top: "2rem" }]}
      />
    );
  return (
    <div>
      <List>
        <List.Item
          onClick={() => inviteModalRef.current.open()}
          children={
            <List.Item.Meta
              title={{
                type: "secondary",
                children: Localization.general.add,
              }}
              avatar={
                <Text level={1}>
                  <Icon component={SVGs.plusHollowCircleFilledComp} />
                </Text>
              }
            />
          }
        />
      </List>
      {!!members.length && (
        <List
          className="m-20-t"
          dataSource={members}
          highlightOnHover={false}
          renderItem={renderItem}
        />
      )}
      <Button
        type="primary"
        onClick={onDone}
        className="m-30-t"
        title={Localization.general.done}
      />
      <Modal
        ref={inviteModalRef}
        title={Localization.general.invite_recipients}
      >
        <Invite
          onConfirm={sendInvites}
          onCancel={() => inviteModalRef.current.close()}
        />
      </Modal>
    </div>
  );
};

export default ClosestCircle;
